import React from 'react';
import { Grid, Box, Divider, Typography, styled } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { sectionTitleStyle, smallGrayFontStyle, extraSmallGrayFontStyle, headerFont, rowsFont } from 'src/styles';
import { Hospital, LessonsDurationByLevel } from 'src/components/Employees/types';
import { EducationLevelGroup } from 'src/constants';

const removeSecondsHandler = (time: string | undefined) => {
    if (time) {
        const timeArray = time.split(':');
        timeArray.pop();
        return timeArray.join(':');
    }
    return '-';
};

const ScheduleGrid: React.FC<LessonsDurationByLevel> = ({ lessonDuration, weekdays }) => {
    const createData = (
        name: string,
        firstLesson: string,
        secondLesson: string,
        thirdLesson: string,
        fourthLesson: string,
        fifthLesson: string,
        sixthLesson: string,
        seventhLesson: string,
        eighthLesson: string,
        ninthLesson?: string,
        tenthLesson?: string
    ) => {
        return {
            name,
            firstLesson,
            secondLesson,
            thirdLesson,
            fourthLesson,
            fifthLesson,
            sixthLesson,
            seventhLesson,
            eighthLesson,
            ninthLesson,
            tenthLesson
        };
    };

    const rows = [
        createData(
            'Початок уроку',
            removeSecondsHandler(weekdays.firstLesson?.from),
            removeSecondsHandler(weekdays.secondLesson?.from),
            removeSecondsHandler(weekdays.thirdLesson?.from),
            removeSecondsHandler(weekdays.fourthLesson?.from),
            removeSecondsHandler(weekdays.fifthLesson?.from),
            removeSecondsHandler(weekdays.sixthLesson?.from),
            removeSecondsHandler(weekdays.seventhLesson?.from),
            removeSecondsHandler(weekdays.eighthLesson?.from),
            removeSecondsHandler(weekdays.ninthLesson?.from),
            removeSecondsHandler(weekdays.tenthLesson?.from)
        ),
        createData(
            'Кінець уроку',
            removeSecondsHandler(weekdays.firstLesson?.to),
            removeSecondsHandler(weekdays.secondLesson?.to),
            removeSecondsHandler(weekdays.thirdLesson?.to),
            removeSecondsHandler(weekdays.fourthLesson?.to),
            removeSecondsHandler(weekdays.fifthLesson?.to),
            removeSecondsHandler(weekdays.sixthLesson?.to),
            removeSecondsHandler(weekdays.seventhLesson?.to),
            removeSecondsHandler(weekdays.eighthLesson?.to),
            removeSecondsHandler(weekdays.ninthLesson?.to),
            removeSecondsHandler(weekdays.tenthLesson?.to)
        )
    ];

    const StyledHeaderTableCell = styled(TableCell)({ ...extraSmallGrayFontStyle, ...headerFont });
    const StyledRowTableCell = styled(TableCell)({ ...extraSmallGrayFontStyle, ...rowsFont });

    return (
        <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }}>
                <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
                    <TableRow>
                        <StyledHeaderTableCell>Тривалість уроку - {lessonDuration} хв</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">1-й урок</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">2-й урок</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">3-й урок</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">4-й урок</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">5-й урок</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">6-й урок</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">7-й урок</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">8-й урок</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">9-й урок</StyledHeaderTableCell>
                        <StyledHeaderTableCell align="right">10-й урок</StyledHeaderTableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow key={row.name} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                            <StyledRowTableCell component="th" scope="row">
                                {row.name}
                            </StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.firstLesson} </StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.secondLesson}</StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.thirdLesson}</StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.fourthLesson}</StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.fifthLesson}</StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.sixthLesson}</StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.seventhLesson}</StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.eighthLesson}</StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.ninthLesson}</StyledRowTableCell>
                            <StyledRowTableCell align="right">{row.tenthLesson}</StyledRowTableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

const HospitalCenterInfo: React.FC<{ hospital: Hospital }> = ({ hospital }) => {
    return (
        <Grid p={1} container columnSpacing={4} rowSpacing={1}>
            <Grid item xs={6}>
                <Box sx={smallGrayFontStyle} mb={1}>
                    Місто
                </Box>
                <Box mb={0.3}>{hospital.city}</Box>
            </Grid>
            <Grid item xs={6}>
                <Box sx={smallGrayFontStyle} mb={1}>
                    Адреса
                </Box>
                <Box mb={0.3}>{hospital.address}</Box>
            </Grid>
            <Grid item xs={6}>
                <Box sx={smallGrayFontStyle} mb={1}>
                    Робочі години
                </Box>
                <Box mb={0.3}>
                    {removeSecondsHandler(hospital.workStart)} - {removeSecondsHandler(hospital.workEnd)}
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box sx={smallGrayFontStyle} mb={1}>
                    Перерва на обід
                </Box>
                <Box mb={0.3}>
                    {removeSecondsHandler(hospital.breakStart)} - {removeSecondsHandler(hospital.breakEnd)}
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Divider sx={{ mb: 2, mt: 2 }} />
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ ...sectionTitleStyle, mb: 3 }} whiteSpace="normal">
                    Тривалість уроків
                </Box>
                <Box mb={4}>
                    <Typography variant="h6" mb={1}>
                        Дошкільна освіта
                    </Typography>
                    <ScheduleGrid
                        lessonDuration={hospital.lessonsDuration.beforeSchoolEducation.lessonDuration}
                        weekdays={hospital.lessonsDuration.beforeSchoolEducation.weekdays}
                        educationLevelGroup={EducationLevelGroup.PRESCHOOL}
                    />
                </Box>
                <Box mb={4}>
                    <Typography variant="h6" mb={1}>
                        1-4 класи
                    </Typography>
                    <ScheduleGrid
                        lessonDuration={hospital.lessonsDuration.firstFourthGrade.lessonDuration}
                        weekdays={hospital.lessonsDuration.firstFourthGrade.weekdays}
                        educationLevelGroup={EducationLevelGroup.JUNIOR_SCHOOL}
                    />
                </Box>
                <Box mb={4}>
                    <Typography variant="h6" mb={1}>
                        5-11 класи
                    </Typography>
                    <ScheduleGrid
                        lessonDuration={hospital.lessonsDuration.fifthTenthGrade.lessonDuration}
                        weekdays={hospital.lessonsDuration.fifthTenthGrade.weekdays}
                        educationLevelGroup={EducationLevelGroup.HIGH_SCHOOL}
                    />
                </Box>
            </Grid>
        </Grid>
    );
};

export default HospitalCenterInfo;
